'use client';

import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import {
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  useActiveFuturesPairs,
  useMarketMenuTickers,
  useOpenFuturesPositionsCount,
  usePairMenuTags,
} from '@/lib/api/futures/hooks';
import { useOpenPositionsCount } from '@/lib/api/history/profile-hooks';
import { useRewardsCounters } from '@/lib/api/rewards-hub/hooks';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { HUB_PATH, LEADERBOARD_PATH } from '@/lib/components/RewardsHub/routes';
import { ZoneLink } from '@/lib/components/ZoneLink';
import { FEATURE_FLAGS } from '@/lib/config/features';
import { useIsHydrated } from '@/lib/hooks/useIsHydrated';
import { useOpenPositionsCountSync } from '@/lib/hooks/useOpenPositionsCountSync';
import { usePathname } from '@/lib/routing';
import { AnimatedIcon } from '@/lib/ui-kit/animated-icon';
import { Icon, type IconName } from '@/lib/ui-kit/icons';
import { ColorIcon } from '@/lib/ui-kit/icons/color';
import { cn } from '@/lib/ui-kit/utils';
import { clamp } from '@/lib/utils/clamp';
import { formatBadgeCount } from '@/lib/utils/formatBadgeCount';
import {
  NO_OVERFLOW,
  readEdges,
  type ScrollEdges,
  sameEdges,
  scrollNav,
  sectionFromPath,
} from '@/lib/utils/headerNav';
import { type FuturesKind, type TradFiRow, tradFiRows } from '@/lib/utils/pairFormat';
import { FuturesPairs } from './FuturesPairs';
import { FuturesPositions } from './FuturesPositions';
import { CARD_LOTTIE_PROPS, FIRE_LOTTIE_PROPS, NAV_ROW_STATE_CLASS } from './navStyles';
import {
  BUY_CRYPTO_PATH,
  CARD_PATH,
  CLAIM_PATH,
  COIN_FUTURES_PATH,
  FUTURES_POSITIONS_PATH,
  HISTORY_PATH,
  POSITIONS_PATH,
  REFERRALS_PATH,
  SIGNUP_PATH,
  USDC_FUTURES_PATH,
  USDT_FUTURES_PATH,
} from './routes';

interface HeaderNavProps {
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  onDeposit?: () => void;
  onConvert?: () => void;
  className?: string;
}

// ── Styles (Figma 14625:42632 nav, 14625:42619 dropdown item) ──

const TRIGGER_CLASS = cn(
  'group flex h-11 cursor-pointer items-center gap-0.5 whitespace-nowrap outline-none',
  'text-[13px] font-medium leading-4 tracking-[-0.13px] text-v2-text-secondary transition-colors duration-150',
  'hover:text-v2-text-primary focus-visible:text-v2-text-primary data-[active]:text-v2-text-primary data-[state=open]:text-v2-text-primary',
);
// One curve and duration for the frame and both panels, so a switch between
// entries moves as a single piece.
const MOTION_CLASS = 'duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:animate-none';
// The panel being left slides out the way the pointer went, the next one in.
const CONTENT_CLASS = cn(
  'absolute top-0 left-0 flex flex-col p-2',
  MOTION_CLASS,
  'data-[motion^=from-]:animate-in data-[motion^=from-]:fade-in-0',
  'data-[motion=from-end]:slide-in-from-right-6 data-[motion=from-start]:slide-in-from-left-6',
  'data-[motion^=to-]:animate-out data-[motion^=to-]:fade-out-0',
  'data-[motion=to-end]:slide-out-to-right-6 data-[motion=to-start]:slide-out-to-left-6',
);
// Glass only in the dark theme; the light one stays solid (product call).
// Radix sizes the frame to the open panel (box-content: the border goes around
// it); `translate` carries it to the entry. The mega menu is rounder.
const VIEWPORT_CLASS = cn(
  'fixed top-0 left-0 z-50 box-content overflow-hidden rounded-[12px] border border-solid',
  'has-[[data-mega-menu]:not([data-motion^=to-])]:rounded-[16px]',
  'h-(--radix-navigation-menu-viewport-height) w-(--radix-navigation-menu-viewport-width)',
  'transition-[translate,width,height] data-[instant]:transition-none motion-reduce:transition-none',
  MOTION_CLASS,
  'border-v2-border bg-v2-surface-primary shadow-[0px_12px_32px_0px_rgba(0,0,0,0.12)]',
  'dark:border-white/10 dark:bg-[rgba(23,26,27,0.5)] dark:shadow-[0px_12px_32px_0px_rgba(0,0,0,0.4)] dark:backdrop-blur-[12px]',
  // Radix writes the size vars a frame after mount: until then the frame stays
  // hidden, and the open animation starts only once it has its real size.
  'not-[[style*=viewport-width]]:opacity-0',
  '[&[data-state=open][style*=viewport-width]]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-1',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
);
const VIEWPORT_GAP_PX = 4;
// A wide panel shifts left rather than run off the screen's right edge.
const VIEWPORT_EDGE_PX = 16;
const PANEL_BORDER_PX = 1;
const ROW_CLASS = cn(
  // 56px rows (Figma) that grow when a translated description wraps.
  'flex min-h-14 w-full cursor-pointer items-center gap-2 rounded-[8px] px-3 py-2 text-left',
  NAV_ROW_STATE_CLASS,
);
const ROW_ICON_SIZE = 20;
const FUTURES_ITEM = 'futures';
const TRADFI_ITEM = 'tradfi';
// Figma 15126:96637, after bitunix: what the bar can't fit scrolls; the arrows
// sit in 20px gutters beside it, and a fade covers the cut edge.
const SCROLL_ARROW_CLASS = cn(
  'absolute top-0 flex h-full w-5 cursor-pointer items-center justify-center text-v2-text-secondary',
  'pointer-events-none opacity-0 transition-[color,opacity] duration-200 hover:text-v2-text-primary',
  'data-[visible]:pointer-events-auto data-[visible]:opacity-100',
);
const SCROLL_FADE_CLASS =
  'pointer-events-none absolute top-0 h-full w-[18px] from-v2-surface-primary to-transparent';

// ── Dropdown parts ──

interface CountBadgeProps {
  count?: number;
}

/** Red counter; none at 0. */
function CountBadge({ count }: CountBadgeProps) {
  if (!count) return null;
  return (
    <span
      translate="no"
      className="flex h-4 shrink-0 items-center rounded-[9px] bg-red-400 px-1 text-[10px] leading-none text-white"
    >
      {formatBadgeCount(count)}
    </span>
  );
}

interface NavRowProps {
  icon: ReactNode;
  title: string;
  description: string;
  /** Red counter after the title; none at 0. */
  badgeCount?: number;
  href?: string;
  /** Id of the note that explains where a guest's row leads. */
  describedBy?: string;
  /** The mega menu's picked row. */
  isSelected?: boolean;
  onSelect?: () => void;
  /** Hover or focus, which picks a mega menu row. */
  onHighlight?: () => void;
}

function NavRow({
  icon,
  title,
  description,
  badgeCount,
  href,
  describedBy,
  isSelected = false,
  onSelect,
  onHighlight,
}: NavRowProps) {
  const body = (
    <>
      <span className="flex size-9 shrink-0 items-center justify-center text-v2-text-primary">
        {icon}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-center gap-1">
          <span className="truncate text-[14px] font-semibold leading-[normal] text-v2-text-primary">
            {title}
          </span>
          <CountBadge count={badgeCount} />
        </span>
        <span className="text-[12px] leading-4 tracking-[-0.12px] text-v2-text-secondary">
          {description}
        </span>
      </span>
    </>
  );

  return (
    <NavigationMenu.Link asChild onSelect={onSelect}>
      {href ? (
        <ZoneLink
          href={href}
          aria-describedby={describedBy}
          data-selected={isSelected || undefined}
          onPointerEnter={onHighlight}
          onFocus={onHighlight}
          className={ROW_CLASS}
        >
          {body}
        </ZoneLink>
      ) : (
        <button type="button" className={ROW_CLASS}>
          {body}
        </button>
      )}
    </NavigationMenu.Link>
  );
}

interface SignUpNoteProps {
  id?: string;
  children: ReactNode;
}

function SignUpNote({ id, children }: SignUpNoteProps) {
  return (
    <NavigationMenu.Link asChild>
      <ZoneLink
        id={id}
        href={SIGNUP_PATH}
        className="block border-t border-solid border-v2-border px-3 pt-3 pb-1 text-[12px] leading-4 text-v2-text-secondary outline-none transition-colors hover:text-v2-text-primary focus-visible:text-v2-text-primary dark:border-white/10"
      >
        {children}
      </ZoneLink>
    </NavigationMenu.Link>
  );
}

interface NavDropdownProps {
  label: string;
  /** The root's value for this entry; a generated id unless the nav needs to know it. */
  value?: string;
  /** Where a click on the entry leads; hover still opens the panel. */
  href?: string;
  /** After the label, before the chevron. */
  adornment?: ReactNode;
  isActive?: boolean;
  /** Figma's outer width, border included: 300, Rewards Hub 280, the mega menus 651. */
  panelWidth?: number;
  contentClass?: string;
  isMegaMenu?: boolean;
  /** The root's open entry: '' closes the panel. */
  onValueChange: (value: string) => void;
  children: ReactNode;
}

function NavDropdown({
  label,
  value: fixedValue,
  href,
  adornment,
  isActive = false,
  panelWidth = 300,
  contentClass,
  isMegaMenu = false,
  onValueChange,
  children,
}: NavDropdownProps) {
  const generatedValue = useId();
  const value = fixedValue ?? generatedValue;
  const triggerBody = (
    <>
      {label}
      {adornment}
      {/* The pseudo-element widens the tap target to the trigger's full height. */}
      <span
        data-nav-chevron
        data-no-nav-progress
        className="relative flex after:absolute after:-inset-y-3.5 after:-left-1.5 after:-right-2"
      >
        <ChevronDown
          aria-hidden
          className="size-4 transition-transform duration-200 group-data-[state=open]:rotate-180"
        />
      </span>
    </>
  );
  const handleNavigate = (event: MouseEvent<HTMLElement>) => {
    // A click on the chevron only opens the panel; preventDefault
    // also skips Radix's toggle, so a second click doesn't close it.
    const isChevron = event.target instanceof Element && event.target.closest('[data-nav-chevron]');
    if (isChevron) {
      event.preventDefault();
      onValueChange(value);
      return;
    }
    // Radix toggles the panel after this handler, which would open a closed one:
    // close it once that's done, before React renders.
    queueMicrotask(() => onValueChange(''));
  };
  // Enter follows the link, so the keyboard opens the panel with the arrow.
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowDown' || event.currentTarget.dataset.state === 'open') return;
    event.preventDefault();
    onValueChange(value);
  };

  return (
    <NavigationMenu.Item value={value}>
      {href ? (
        <NavigationMenu.Trigger
          asChild
          className={TRIGGER_CLASS}
          data-active={isActive || undefined}
          data-nav-trigger
          data-panel-width={panelWidth}
          onClick={handleNavigate}
          onKeyDown={handleKeyDown}
        >
          <ZoneLink href={href}>{triggerBody}</ZoneLink>
        </NavigationMenu.Trigger>
      ) : (
        <NavigationMenu.Trigger
          className={TRIGGER_CLASS}
          data-active={isActive || undefined}
          data-nav-trigger
          data-panel-width={panelWidth}
        >
          {triggerBody}
        </NavigationMenu.Trigger>
      )}
      <NavigationMenu.Content
        data-mega-menu={isMegaMenu || undefined}
        style={{ width: panelWidth - PANEL_BORDER_PX * 2 }}
        className={cn(CONTENT_CLASS, contentClass)}
      >
        {children}
      </NavigationMenu.Content>
    </NavigationMenu.Item>
  );
}

// ── Sections ──
// Each panel mounts only while open; its counters are warmed by the nav, so they show at once.

interface AuthRowsProps {
  isAuthenticated: boolean;
  // Not the inverse of isAuthenticated: while the session probe runs it's neither.
  isGuest: boolean;
}

interface BuyCryptoRowsProps
  extends AuthRowsProps,
    Pick<HeaderNavProps, 'onDeposit' | 'onConvert'> {}

function BuyCryptoRows({ isAuthenticated, isGuest, onDeposit, onConvert }: BuyCryptoRowsProps) {
  const t = useTranslations('nav.menu');
  // A guest's every row leads to sign-up.
  const guestHref = isGuest ? SIGNUP_PATH : undefined;
  const noteId = useId();
  const describedBy = isGuest ? noteId : undefined;

  return (
    <>
      <NavRow
        icon={<Icon name="NavFlash" size={ROW_ICON_SIZE} />}
        title={t('oneClickBuy.title')}
        description={t('oneClickBuy.description')}
        href={guestHref ?? BUY_CRYPTO_PATH}
        describedBy={describedBy}
      />
      <NavRow
        icon={<Icon name="Download" size={ROW_ICON_SIZE} />}
        title={t('cryptoDeposit.title')}
        description={t('cryptoDeposit.description')}
        href={guestHref}
        describedBy={describedBy}
        onSelect={isAuthenticated ? onDeposit : undefined}
      />
      <NavRow
        icon={<Icon name="BalanceConvert" size={ROW_ICON_SIZE} />}
        title={t('convert.title')}
        description={t('convert.description')}
        href={guestHref}
        describedBy={describedBy}
        onSelect={isAuthenticated ? onConvert : undefined}
      />
      {isGuest ? <SignUpNote id={noteId}>{t('buyCryptoCta')}</SignUpNote> : null}
    </>
  );
}

function PositionsRows() {
  const t = useTranslations('nav.menu');
  const { user } = useAuthContext();
  const { count: positionsCount } = useOpenPositionsCount(user?.id ?? '');

  return (
    <>
      <NavRow
        icon={<ColorIcon name="NavPositions" size={ROW_ICON_SIZE} />}
        title={t('myPositions.title')}
        description={t('myPositions.description')}
        badgeCount={positionsCount}
        href={POSITIONS_PATH}
      />
      <NavRow
        icon={<Icon name="Clock" size={ROW_ICON_SIZE} />}
        title={t('history.title')}
        description={t('history.description')}
        href={HISTORY_PATH}
      />
    </>
  );
}

function PredictionMarketsRows({ isAuthenticated, isGuest }: AuthRowsProps) {
  const t = useTranslations('nav.menu');
  return (
    <>
      <NavRow
        icon={<ColorIcon name="NavMarkets" size={ROW_ICON_SIZE} />}
        title={t('markets.title')}
        description={t('markets.description')}
        href="/"
      />
      {isAuthenticated ? <PositionsRows /> : null}
      {isGuest ? <SignUpNote>{t('predictionsCta')}</SignUpNote> : null}
    </>
  );
}

interface FuturesKindItem {
  kind: FuturesKind;
  icon: IconName;
  label: 'usdtFutures' | 'usdcFutures' | 'coinFutures';
  href: string;
}

const FUTURES_KINDS: FuturesKindItem[] = [
  { kind: 'usdt', icon: 'NavUsdt', label: 'usdtFutures', href: USDT_FUTURES_PATH },
  { kind: 'usdc', icon: 'NavUsdc', label: 'usdcFutures', href: USDC_FUTURES_PATH },
  { kind: 'inverse', icon: 'NavCoin', label: 'coinFutures', href: COIN_FUTURES_PATH },
];

/** What the Futures menu's right column shows: a kind's pairs or the open positions. */
type FuturesView = FuturesKind | 'positions';

interface FuturesKindRowProps {
  item: FuturesKindItem;
  isSelected: boolean;
  onPick: (view: FuturesView) => void;
}

function FuturesKindRow({ item, isSelected, onPick }: FuturesKindRowProps) {
  const t = useTranslations('nav.menu');
  const handleHighlight = () => onPick(item.kind);
  return (
    <NavRow
      icon={<Icon name={item.icon} size={ROW_ICON_SIZE} />}
      title={t(`${item.label}.title`)}
      description={t(`${item.label}.description`)}
      href={item.href}
      isSelected={isSelected}
      onHighlight={handleHighlight}
    />
  );
}

type FuturesPositionsRowProps = Omit<FuturesKindRowProps, 'item'>;

function FuturesPositionsRow({ isSelected, onPick }: FuturesPositionsRowProps) {
  const t = useTranslations('nav.menu');
  const { count: positionsCount } = useOpenFuturesPositionsCount();
  const handleHighlight = () => onPick('positions');

  return (
    <NavRow
      icon={<ColorIcon name="NavPositions" size={ROW_ICON_SIZE} />}
      title={t('myFutures.title')}
      description={t('myFutures.description')}
      badgeCount={positionsCount}
      href={FUTURES_POSITIONS_PATH}
      isSelected={isSelected}
      onHighlight={handleHighlight}
    />
  );
}

type FuturesMenuProps = Pick<AuthRowsProps, 'isAuthenticated'>;

/** Figma 15071:148044: the rows on the left pick the right column, a kind's pairs or the positions. */
function FuturesMenu({ isAuthenticated }: FuturesMenuProps) {
  const [view, setView] = useState<FuturesView>('usdt');
  return (
    <>
      <div className="flex w-[300px] shrink-0 flex-col gap-1">
        {FUTURES_KINDS.map((item) => (
          <FuturesKindRow
            key={item.kind}
            item={item}
            isSelected={item.kind === view}
            onPick={setView}
          />
        ))}
        {isAuthenticated ? (
          <FuturesPositionsRow isSelected={view === 'positions'} onPick={setView} />
        ) : null}
      </div>
      <div aria-hidden className="w-px shrink-0 self-stretch bg-v2-border dark:bg-white/10" />
      {view === 'positions' ? <FuturesPositions /> : <FuturesPairs market={view} />}
    </>
  );
}

interface TradFiTagCopy {
  label: 'stocks' | 'commodities' | 'etfs';
  icon: IconName;
}

/** Our copy of the feed's tags; a tag new to the admin shows its English name until it has one. */
const TRADFI_TAG_COPY: Record<string, TradFiTagCopy> = {
  STOCK: { label: 'stocks', icon: 'Stock' },
  COMMODITY: { label: 'commodities', icon: 'Commodity' },
  ETF: { label: 'etfs', icon: 'Etf' },
};

interface TradFiTagRowProps {
  row: TradFiRow;
  isSelected: boolean;
  onPick: (code: string) => void;
}

function TradFiTagRow({ row, isSelected, onPick }: TradFiTagRowProps) {
  const t = useTranslations('nav.menu');
  const copy = TRADFI_TAG_COPY[row.code];
  const title = copy ? t(`${copy.label}.title`) : row.name;
  const description = copy ? t(`${copy.label}.description`) : '';
  const handleHighlight = () => onPick(row.code);

  return (
    <NavRow
      icon={<Icon name={copy?.icon ?? 'Stock'} size={ROW_ICON_SIZE} />}
      title={title}
      description={description}
      href={row.firstPairPath}
      isSelected={isSelected}
      onHighlight={handleHighlight}
    />
  );
}

interface TradFiMenuProps {
  rows: TradFiRow[];
}

/** Figma 15071:148024: the admin's tags on the left pick the pair list on the right. */
function TradFiMenu({ rows }: TradFiMenuProps) {
  const [code, setCode] = useState<string>();
  const selected = rows.find((row) => row.code === code) ?? rows[0];

  return (
    <>
      <div className="flex w-[264px] shrink-0 flex-col gap-1">
        {rows.map((row) => (
          <TradFiTagRow key={row.code} row={row} isSelected={row === selected} onPick={setCode} />
        ))}
      </div>
      <div aria-hidden className="w-px shrink-0 self-stretch bg-v2-border dark:bg-white/10" />
      <FuturesPairs market={selected?.market ?? 'tradfi'} className="w-[360px]" />
    </>
  );
}

type TradFiDropdownProps = Pick<NavDropdownProps, 'onValueChange'>;

// Mounted only with the trade zone on: the entry needs the pairs before its panel opens.
function TradFiDropdown({ onValueChange }: TradFiDropdownProps) {
  const t = useTranslations('nav');
  const { data: pairs } = useActiveFuturesPairs();
  const { data: tags } = usePairMenuTags();
  const isHydrated = useIsHydrated();
  // The server renders the header without the page's prefetched pairs: rows built
  // before hydration would turn the server's <button> trigger into a link.
  const rows = isHydrated && pairs && tags !== undefined ? tradFiRows(pairs, tags) : [];

  return (
    <NavDropdown
      label={t('tradFi')}
      value={TRADFI_ITEM}
      href={rows[0]?.firstPairPath}
      panelWidth={675}
      contentClass="flex-row gap-2 p-4"
      isMegaMenu
      onValueChange={onValueChange}
    >
      <TradFiMenu rows={rows} />
    </NavDropdown>
  );
}

// The panel's row is unseen until it opens, so the closed entry carries the count too.
function RewardsTriggerBadge({ isAuthenticated }: Pick<AuthRowsProps, 'isAuthenticated'>) {
  const { data: readyCount = 0 } = useRewardsCounters(isAuthenticated);
  return <CountBadge count={readyCount} />;
}

function RewardsRows({ isAuthenticated, isGuest }: AuthRowsProps) {
  const t = useTranslations('nav.menu');
  const { data: readyCount = 0 } = useRewardsCounters(isAuthenticated);

  return (
    <>
      <NavRow
        icon={<Icon name="NavTasks" size={ROW_ICON_SIZE} />}
        title={t('allTasks.title')}
        description={t('allTasks.description')}
        href={HUB_PATH}
      />
      {isAuthenticated ? (
        <NavRow
          icon={<Icon name="AlertRewardsHub" size={ROW_ICON_SIZE} />}
          title={t('claimRewards.title')}
          description={t('claimRewards.description')}
          badgeCount={readyCount}
          href={CLAIM_PATH}
        />
      ) : null}
      <NavRow
        icon={<ColorIcon name="NavLeaderboard" size={ROW_ICON_SIZE} />}
        title={t('leaderboard.title')}
        description={t('leaderboard.description')}
        href={LEADERBOARD_PATH}
      />
      {isGuest ? <SignUpNote>{t('rewardsCta')}</SignUpNote> : null}
    </>
  );
}

interface MarketMenuTickersProps {
  isMenuOpen: boolean;
}

// A leaf of its own: the price writes re-render it, not the nav.
function MarketMenuTickers({ isMenuOpen }: MarketMenuTickersProps) {
  useMarketMenuTickers(isMenuOpen);
  return null;
}

// Fetched with the nav, not on open: the My positions badge is in the panel's first frame.
function OpenPositionsCountWarmup() {
  const { user } = useAuthContext();
  const userId = user?.id ?? '';
  const { firstPage } = useOpenPositionsCount(userId);
  useOpenPositionsCountSync(userId, firstPage?.items);
  return null;
}

// Same for the Futures panel's My positions badge.
function OpenFuturesPositionsCountWarmup() {
  useOpenFuturesPositionsCount();
  return null;
}

// ── Nav ──

/**
 * The header's primary nav (Figma 14625:42632, dropdowns 9084:89506): hover
 * opens an entry's panel, a click goes to its page; the guest's panels end on a sign-up note.
 */
export function HeaderNav({
  isAuthenticated,
  isAuthLoading,
  onDeposit,
  onConvert,
  className,
}: HeaderNavProps) {
  const rootRef = useRef<HTMLElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const prevOpenItemRef = useRef('');
  const [openItem, setOpenItem] = useState('');
  const [edges, setEdges] = useState<ScrollEdges>(NO_OVERFLOW);
  const t = useTranslations('nav');
  const pathname = usePathname();
  const isHydrated = useIsHydrated();

  // An open panel is placed once, under its entry: scrolling the bar closes it.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const update = () => setEdges((prev) => sameEdges(prev, readEdges(list)));
    const handleScroll = () => {
      update();
      setOpenItem('');
    };
    // The items too: a label or icon that grows changes the overflow, not the bar.
    const observer = new ResizeObserver(update);
    observer.observe(list);
    for (const item of list.children) observer.observe(item);
    list.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      observer.disconnect();
      list.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // The panel lives in <body>: inside the header's z-index layer backdrop-filter
  // has none of the page to blur. Placed on the node, not through state, so a
  // switch between entries costs no extra render.
  const placeViewport = useCallback((viewport: HTMLElement, isInstant: boolean) => {
    const trigger = rootRef.current?.querySelector('[data-nav-trigger][data-state="open"]');
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const panelWidth = Number(trigger.getAttribute('data-panel-width'));
    const maxLeft = window.innerWidth - panelWidth - VIEWPORT_EDGE_PX;
    const left = clamp(rect.left, VIEWPORT_EDGE_PX, maxLeft);
    viewport.toggleAttribute('data-instant', isInstant);
    viewport.style.translate = `${left}px ${rect.bottom + VIEWPORT_GAP_PX}px`;
  }, []);

  // Radix mounts the viewport a commit after the menu opens: it appears in place.
  const handleViewportRef = useCallback(
    (node: HTMLDivElement | null) => {
      viewportRef.current = node;
      if (node) placeViewport(node, true);
    },
    [placeViewport],
  );

  useLayoutEffect(() => {
    const wasOpen = prevOpenItemRef.current !== '';
    prevOpenItemRef.current = openItem;
    if (openItem && viewportRef.current) placeViewport(viewportRef.current, !wasOpen);
  }, [openItem, placeViewport]);

  const activeSection = sectionFromPath(pathname ?? '');
  // A gutter only where its arrow shows: at scroll-start the bar stays flush with the logo.
  const rootClass = cn('relative h-full', edges.start && 'pl-5', edges.end && 'pr-5', className);
  const handleScrollStart = () => scrollNav(listRef.current, -1);
  const handleScrollEnd = () => scrollNav(listRef.current, 1);
  // Every full page load re-probes the session: until it answers a signed-in
  // player isn't a guest, or a click in that window lands on sign-up.
  const isGuest = !isAuthenticated && !isAuthLoading;
  const isMarketMenuOpen = openItem === FUTURES_ITEM || openItem === TRADFI_ITEM;

  return (
    <NavigationMenu.Root
      ref={rootRef}
      aria-label="Primary"
      value={openItem}
      onValueChange={setOpenItem}
      delayDuration={100}
      className={rootClass}
    >
      {FEATURE_FLAGS.ENABLE_TRADE_ZONE ? <MarketMenuTickers isMenuOpen={isMarketMenuOpen} /> : null}
      {isAuthenticated ? <OpenPositionsCountWarmup /> : null}
      {FEATURE_FLAGS.ENABLE_TRADE_ZONE && isAuthenticated ? (
        <OpenFuturesPositionsCountWarmup />
      ) : null}
      <NavigationMenu.List
        ref={listRef}
        className="flex h-full items-center gap-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <NavDropdown
          label={t('buyCrypto')}
          href={isGuest ? SIGNUP_PATH : BUY_CRYPTO_PATH}
          onValueChange={setOpenItem}
        >
          <BuyCryptoRows
            isAuthenticated={isAuthenticated}
            isGuest={isGuest}
            onDeposit={onDeposit}
            onConvert={onConvert}
          />
        </NavDropdown>
        <NavDropdown
          label={t('predictionMarkets')}
          href="/"
          isActive={activeSection === 'predictionMarkets'}
          onValueChange={setOpenItem}
        >
          <PredictionMarketsRows isAuthenticated={isAuthenticated} isGuest={isGuest} />
        </NavDropdown>
        {FEATURE_FLAGS.ENABLE_TRADE_ZONE ? (
          <NavDropdown
            label={t('futures')}
            value={FUTURES_ITEM}
            adornment={
              <AnimatedIcon
                name="nav-fire"
                size={16}
                loop
                playOnHover={false}
                lottieProps={FIRE_LOTTIE_PROPS}
              />
            }
            href={USDT_FUTURES_PATH}
            isActive={activeSection === 'futures'}
            panelWidth={651}
            contentClass="flex-row gap-2 p-4"
            isMegaMenu
            onValueChange={setOpenItem}
          >
            <FuturesMenu isAuthenticated={isAuthenticated} />
          </NavDropdown>
        ) : null}
        {FEATURE_FLAGS.ENABLE_TRADE_ZONE ? <TradFiDropdown onValueChange={setOpenItem} /> : null}
        <NavDropdown
          label={t('rewardsHub')}
          href={HUB_PATH}
          adornment={<RewardsTriggerBadge isAuthenticated={isAuthenticated} />}
          isActive={activeSection === 'rewards'}
          panelWidth={280}
          contentClass="gap-0.5"
          onValueChange={setOpenItem}
        >
          <RewardsRows isAuthenticated={isAuthenticated} isGuest={isGuest} />
        </NavDropdown>
        <NavigationMenu.Item>
          <NavigationMenu.Link asChild>
            {/* /account sends a guest home, so a guest's Referral is sign-up. */}
            <ZoneLink href={isGuest ? SIGNUP_PATH : REFERRALS_PATH} className={TRIGGER_CLASS}>
              {t('referral')}
            </ZoneLink>
          </NavigationMenu.Link>
        </NavigationMenu.Item>
        {FEATURE_FLAGS.ENABLE_CARD_NAV ? (
          <NavigationMenu.Item>
            <NavigationMenu.Link asChild>
              <ZoneLink href={CARD_PATH} className={cn(TRIGGER_CLASS, 'gap-1')}>
                {t('card')}
                <AnimatedIcon
                  name="nav-card-flip"
                  size={20}
                  loop
                  playOnHover={false}
                  lottieProps={CARD_LOTTIE_PROPS}
                />
              </ZoneLink>
            </NavigationMenu.Link>
          </NavigationMenu.Item>
        ) : null}
      </NavigationMenu.List>
      {/* Pointer helpers only: Tab already scrolls a focused entry into view. */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        data-visible={edges.start || undefined}
        onClick={handleScrollStart}
        className={cn(SCROLL_ARROW_CLASS, 'left-0')}
      >
        <ChevronLeft className="size-4" />
        <span className={cn(SCROLL_FADE_CLASS, 'left-full bg-gradient-to-r')} />
      </button>
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        data-visible={edges.end || undefined}
        onClick={handleScrollEnd}
        className={cn(SCROLL_ARROW_CLASS, 'right-0')}
      >
        <ChevronRight className="size-4" />
        <span className={cn(SCROLL_FADE_CLASS, 'right-full bg-gradient-to-l')} />
      </button>
      {isHydrated
        ? createPortal(
            <NavigationMenu.Viewport ref={handleViewportRef} className={VIEWPORT_CLASS} />,
            document.body,
          )
        : null}
    </NavigationMenu.Root>
  );
}
